import type React from 'react';

import type * as api from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import { Button } from '@gitbook/spine-react';

import { extractContentLocationInfo, useNavigateToContentLocation } from '../../../state';

/**
 * Render a simple list of backlinks (content locations) with navigation.
 */
export function BacklinksList(props: {
    contentLocations: api.ContentLocation[];
    onNavigate: () => void;
}): React.ReactElement {
    const { contentLocations, onNavigate } = props;

    const navigateToBackLink = useNavigateToContentLocation();

    // group content locations by id
    const contentLocationsGrouped = contentLocations.reduce((groups, contentLocation) => {
        const { id } = extractContentLocationInfo(contentLocation);

        if (!groups[id]) {
            groups[id] = [];
        }

        groups[id].push(contentLocation);

        return groups;
    }, {});

    return (
        <div className="flex flex-wrap gap-2">
            {Object.keys(contentLocationsGrouped).map((contentLocationId) => {
                // use the first content location to get label and navigation info
                // next step: use the count to show the number of backlinks
                const firstContentLocation = contentLocationsGrouped[contentLocationId][0];
                const contentLocationCount = contentLocationsGrouped[contentLocationId].length;

                const { id: key, text: label } = extractContentLocationInfo(firstContentLocation);

                return (
                    <Button
                        key={key}
                        kind="secondary"
                        size="small"
                        leadingIcon={Icon.PageLink}
                        onPress={() => {
                            navigateToBackLink(firstContentLocation).then(onNavigate);
                        }}
                        label={label}
                        badge={
                            contentLocationCount > 1
                                ? {
                                      label: contentLocationCount,
                                      kind: 'secondary',
                                  }
                                : undefined
                        }
                    />
                );
            })}
        </div>
    );
}
